import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { upgradeApi } from '../../services/api';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import './Upgrade.css';

const Upgrade = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [upgraded] = useState(user?.subscription?.status === 'active');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const navigate = useNavigate();
  const location = useLocation();

  // Check for success or canceled query params from Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      // Verify the checkout session was completed successfully
      const verifyCheckout = async () => {
        try {
          await upgradeApi.getCheckoutSession(sessionId);
          toast.success('Your subscription has been activated!');
          // Redirect to remove query params
          navigate('/dashboard/upgrade', { replace: true });
          // Refresh user data to show updated subscription status
          window.location.reload();
        } catch (err) {
          toast.error('Unable to verify your subscription. Please contact support.');
        }
      };
      verifyCheckout();
    }
    
    if (params.has('canceled')) {
      toast.info('Your subscription process was canceled.');
      navigate('/dashboard/upgrade', { replace: true });
    }
  }, [location, navigate]);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await upgradeApi.upgradeToPro(selectedPlan);
      
      // Redirect to Stripe Checkout
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Could not create checkout session. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="upgrade-page">
      <div className="upgrade-card">
        <h1>Upgrade Your Plan</h1>
        <p className="upgrade-lead">Unlock unlimited link creation and advanced analytics by upgrading to our <strong>Pro</strong> plan for just <strong>$9/month</strong> or <strong>$90/year</strong> (2 months free)!</p>
        <div className="plan-selector">
          <button 
            className={`plan-option ${selectedPlan === 'monthly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('monthly')}
          >
            Monthly: $9/month
          </button>
          <button 
            className={`plan-option ${selectedPlan === 'yearly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('yearly')}
          >
            Yearly: $90/year <span className="savings-badge">Save 16%</span>
          </button>
        </div>
        
        <div className="plan-table">
          <div className="plan-row plan-header">
            <div>Feature</div>
            <div>Free</div>
            <div>Pro <span className="plan-price">{selectedPlan === 'monthly' ? '$9/mo' : '$90/yr'}</span></div>
          </div>
          <div className="plan-row">
            <div>Affiliate Links</div>
            <div>3</div>
            <div>Unlimited</div>
          </div>
          <div className="plan-row">
            <div>Clicks/Month</div>
            <div>1,000</div>
            <div>Unlimited</div>
          </div>
          <div className="plan-row">
            <div>Conversions/Month</div>
            <div>50</div>
            <div>Unlimited</div>
          </div>
          <div className="plan-row">
            <div>Dashboard</div>
            <div>Basic</div>
            <div>Advanced</div>
          </div>
          <div className="plan-row">
            <div>Export Data</div>
            <div>No</div>
            <div>Yes</div>
          </div>
          <div className="plan-row">
            <div>Priority Support</div>
            <div>No</div>
            <div>Yes</div>
          </div>
        </div>
        <div className="upgrade-action">
          {upgraded ? (
            <div className="already-upgraded">You are already on the Pro plan. Thank you!</div>
          ) : (
            <button className="btn btn-primary" onClick={handleUpgrade} disabled={loading}>
              {loading ? 'Redirecting to secure checkout...' : `Upgrade to Pro (${selectedPlan === 'monthly' ? '$9/month' : '$90/year'})`}
            </button>
          )}
        </div>
        <p className="upgrade-note">Secure payment processing by <strong>Stripe</strong>. You can cancel your subscription anytime from your account page. For questions or support, contact <a href="mailto:support@example.com">support@example.com</a>.</p>
      </div>
    </div>
  );
};

export default Upgrade;
